import React, { useState, useEffect, useCallback } from "react";
import AdminLayout from "../../components/admin/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import { CheckCircle2, XCircle, Search, ChevronDown, Disc3, ExternalLink } from "lucide-react";
import api from "../../utils/api";
import { toast } from "sonner";

function ReviewModal({ app, token, onClose, onReviewed }) {
  const [status, setStatus]       = useState("approved");
  const [note, setNote]           = useState("");
  const [labelSearch, setLabelSearch] = useState(app.existing_label_name || "");
  const [labelResults, setLabelResults] = useState([]);
  const [selectedLabel, setSelectedLabel] = useState(
    app.existing_label_id ? { id: app.existing_label_id, name: app.existing_label_name } : null
  );
  const [saving, setSaving] = useState(false);

  const searchLabels = async () => {
    if (!labelSearch.trim()) return;
    try {
      const res = await api.get(`/labels/admin/all?search=${encodeURIComponent(labelSearch)}&limit=8`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLabelResults(res.data.labels || []);
    } catch {}
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.put(`/label-applications/admin/${app.id}`, {
        status,
        reviewer_note: note || null,
        label_id: selectedLabel?.id || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(status === "approved" ? "Bewerbung genehmigt" : "Bewerbung abgelehnt");
      onReviewed();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
    setSaving(false);
  };

  const inp = "w-full px-3 py-2 rounded-lg text-sm border focus:outline-none bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500";

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        className="w-full max-w-lg rounded-2xl shadow-2xl bg-zinc-900 text-white border border-white/10 flex flex-col max-h-[90vh]">
        <div className="p-5 border-b border-white/10">
          <h2 className="font-black text-lg font-heading">Bewerbung prüfen</h2>
          <p className="text-sm text-zinc-400 mt-1">von {app.username || app.email}</p>
        </div>
        <div className="overflow-y-auto p-5 space-y-4 flex-1">
          {/* Info */}
          <div className="p-4 rounded-xl space-y-2 bg-zinc-800/60">
            <p className="text-xs text-zinc-400 font-semibold uppercase tracking-wide">Angaben des Bewerbers</p>
            <p className="text-sm"><span className="text-zinc-400">Label:</span> <strong>{app.label_name}</strong></p>
            {app.existing_label_name && <p className="text-sm"><span className="text-zinc-400">Vorschlag:</span> {app.existing_label_name}</p>}
            <p className="text-sm"><span className="text-zinc-400">E-Mail:</span> {app.contact_email}</p>
            {app.website && <p className="text-sm"><span className="text-zinc-400">Website:</span> {app.website}</p>}
            <p className="text-sm text-zinc-300 mt-2 border-t border-white/10 pt-2">{app.info_text}</p>
          </div>

          {/* Status */}
          <div className="grid grid-cols-2 gap-2">
            {[["approved","Genehmigen"],["rejected","Ablehnen"]].map(([v, l]) => (
              <button key={v} onClick={() => setStatus(v)}
                className={`py-2.5 rounded-xl text-sm font-semibold border transition-colors ${
                  status === v
                    ? v === "approved" ? "bg-emerald-600 border-emerald-600 text-white" : "bg-red-600 border-red-600 text-white"
                    : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:text-white"
                }`}>
                {l}
              </button>
            ))}
          </div>

          {/* Label-Zuweisung bei Approve */}
          {status === "approved" && (
            <div className="space-y-2">
              <label className="text-xs text-zinc-400 font-semibold uppercase tracking-wide block">Label zuweisen</label>
              {selectedLabel ? (
                <div className="flex items-center justify-between p-3 rounded-xl border bg-zinc-800 border-zinc-700">
                  <div className="flex items-center gap-2">
                    <Disc3 size={14} className="text-zinc-400" />
                    <span className="text-sm font-medium">{selectedLabel.name}</span>
                  </div>
                  <button onClick={() => setSelectedLabel(null)} className="text-zinc-500 hover:text-red-400 transition-colors">
                    <XCircle size={15} />
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input className={`${inp} flex-1`} value={labelSearch}
                    onChange={e => setLabelSearch(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && searchLabels()}
                    placeholder="Label suchen..." />
                  <button onClick={searchLabels} className="px-3 py-2 bg-zinc-700 rounded-lg text-sm text-white hover:bg-zinc-600">
                    <Search size={14} />
                  </button>
                </div>
              )}
              {!selectedLabel && labelResults.length > 0 && (
                <div className="space-y-1 max-h-32 overflow-y-auto rounded-xl border border-zinc-700 bg-zinc-800">
                  {labelResults.map(l => (
                    <button key={l.id} onClick={() => { setSelectedLabel(l); setLabelResults([]); }}
                      className="w-full text-left px-3 py-2 text-sm transition-colors hover:bg-zinc-700 text-zinc-300">
                      {l.name} <span className="text-xs text-zinc-500">({l.release_count})</span>
                    </button>
                  ))}
                </div>
              )}
              <p className="text-xs text-zinc-500">Wird kein Label ausgewählt, muss der Manager manuell im Label-Admin zugewiesen werden.</p>
            </div>
          )}

          {/* Note */}
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Interne Notiz (optional)</label>
            <textarea className={`${inp} resize-none`} rows={2} value={note}
              onChange={e => setNote(e.target.value)} placeholder="Nur intern sichtbar..." />
          </div>
        </div>
        <div className="p-5 border-t border-white/10 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl bg-zinc-800 text-zinc-300 hover:bg-zinc-700">Abbrechen</button>
          <button onClick={submit} disabled={saving}
            className={`px-4 py-2 text-sm font-semibold rounded-xl text-white disabled:opacity-50 ${status === "approved" ? "bg-emerald-600 hover:bg-emerald-500" : "bg-red-600 hover:bg-red-500"}`}>
            {saving ? "Speichert..." : status === "approved" ? "Genehmigen" : "Ablehnen"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function LabelApplicationsAdminPage() {
  const { token }   = useAuth();

  const [apps, setApps]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [statusFilter, setStatus] = useState("pending");
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(false);
  const [reviewing, setReviewing] = useState(null);
  const LIMIT = 20;

  const fetchApps = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await api.get(`/label-applications/admin?status=${statusFilter}&page=${page}&limit=${LIMIT}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setApps(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {}
    setLoading(false);
  }, [token, statusFilter, page]);

  useEffect(() => { fetchApps(); }, [fetchApps]);

  const statusBadge = (s) => {
    if (s === "pending")  return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/15 text-amber-400 border border-amber-500/20">OFFEN</span>;
    if (s === "approved") return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">GENEHMIGT</span>;
    return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500/15 text-red-400 border border-red-500/20">ABGELEHNT</span>;
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-black text-white font-heading">Label-Bewerbungen</h1>
            <p className="text-sm text-zinc-500 mt-0.5">{total} Bewerbungen</p>
          </div>
        </div>

        {/* Filter */}
        <div className="flex gap-2 mb-6">
          {[["pending","Offen"],["approved","Genehmigt"],["rejected","Abgelehnt"],["all","Alle"]].map(([v, l]) => (
            <button key={v} onClick={() => { setStatus(v); setPage(1); }}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${statusFilter === v ? "bg-rose-600 text-white" : "bg-zinc-800 text-zinc-400 hover:text-white"}`}>
              {l}
            </button>
          ))}
        </div>

        {/* List */}
        <div className="bg-zinc-900 border border-white/5 rounded-xl overflow-hidden">
          {loading ? (
            <div className="text-center py-12 text-zinc-500">Lädt...</div>
          ) : apps.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">Keine Bewerbungen gefunden.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs font-semibold uppercase tracking-wide text-zinc-400 border-b border-white/5">
                  <th className="text-left px-4 py-3">Bewerber</th>
                  <th className="text-left px-4 py-3">Label-Name</th>
                  <th className="text-left px-4 py-3">Vorschlag</th>
                  <th className="text-center px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Datum</th>
                  <th className="text-right px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {apps.map(app => (
                  <tr key={app.id} className="hover:bg-white/[0.02] transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-medium text-white">{app.username || "–"}</p>
                      <p className="text-xs text-zinc-500">{app.email}</p>
                    </td>
                    <td className="px-4 py-3 font-semibold text-white">{app.label_name}</td>
                    <td className="px-4 py-3 text-xs text-zinc-400">{app.existing_label_name || "–"}</td>
                    <td className="px-4 py-3 text-center">{statusBadge(app.status)}</td>
                    <td className="px-4 py-3 text-right text-xs text-zinc-500">
                      {app.created_at ? new Date(app.created_at).toLocaleDateString("de-DE") : "–"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {app.status === "pending" && (
                        <button onClick={() => setReviewing(app)} data-testid={`app-review-${app.id}`}
                          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-rose-600 hover:bg-rose-500 text-white transition-colors">
                          Prüfen
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {total > LIMIT && (
          <div className="flex justify-center gap-2 mt-6">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1.5 text-sm rounded-lg bg-zinc-800 text-zinc-400 hover:text-white disabled:opacity-40 transition">Zurück</button>
            <span className="px-3 py-1.5 text-sm text-zinc-400">{page} / {Math.ceil(total / LIMIT)}</span>
            <button onClick={() => setPage(p => p + 1)} disabled={page * LIMIT >= total}
              className="px-3 py-1.5 text-sm rounded-lg bg-zinc-800 text-zinc-400 hover:text-white disabled:opacity-40 transition">Weiter</button>
          </div>
        )}
      </div>

      {reviewing && (
        <ReviewModal app={reviewing} token={token}
          onClose={() => setReviewing(null)} onReviewed={() => { setReviewing(null); fetchApps(); }} />
      )}
    </AdminLayout>
  );
}
